import {
  Box,
  Button,
  Dialog,
  Switch,
  Backdrop,
  useTheme,
  TextField,
  FormLabel,
  Typography,
  DialogTitle,
  Autocomplete,
  Grid2 as Grid,
  DialogActions,
  DialogContent,
  svgIconClasses,
  FormHelperText,
  CircularProgress,
  FormControlLabel,
  DialogContentText,
} from '@mui/material';
import * as yup from 'yup';
import dayjs, { Dayjs } from 'dayjs';
import { Book } from 'pages/dashboard';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { EFFORT_STATUS_TYPE_ENUM } from 'enums';
import { isPastMonthOvertime } from '../../utils';
import { DateTimePicker } from '@mui/x-date-pickers';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormattedMessage, useIntl } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import loaderLogoSrc from 'assets/images/idecon-loader-logo.png';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Authentication, renderAutoCompleteLocaleText, renderDatePickerLocaleText } from 'utils';
import { Project, ActivityType, ActivityBoardItem, EffortItem, User, HolidayItem } from 'library/types';

type CreateActivityBoardItemFormParams = {
  bookId: Book | null;
  projectId: Project | null;
  activityTypeId: ActivityType | null;
  effortTrackNo: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  estimatedHours: string;
  description: string;
  isOvertimeWork: boolean;
};

export type CreateActivityBoardItemPostFormParams = {
  bookId: number;
  projectId: number;
  activityTypeId: number;
  effortTrackNo: string;
  startDate: string | null;
  endDate: string | null;
  estimatedHours: number;
  description: string;
  isOvertimeWork: boolean;
};

const defaultValues: CreateActivityBoardItemFormParams = {
  bookId: null,
  projectId: null,
  activityTypeId: null,
  effortTrackNo: '',
  startDate: null,
  endDate: null,
  estimatedHours: '',
  description: '',
  isOvertimeWork: false,
};

const END_DATE_QUICK_ADD_MINUTES = [15, 30, 60] as const;

export type InitialActivityData = {
  bookId?: number | null;
  projectId?: number | null;
  effortTrackNo?: string | null;
};

type CreateActivityBoardItemDialogProps = {
  open: boolean;
  handleClose: () => void;
  books: Book[];
  products: Project[];
  activityTypes: ActivityType[];
  users?: User[];
  refresh?: () => Promise<void>;
  initialData?: InitialActivityData | null;
  onSuccess?: () => void;
};

const CreateActivityBoardItemDialog = ({
  open,
  handleClose: closeDialog,
  books,
  products,
  activityTypes,
  users = [],
  refresh,
  initialData,
  onSuccess,
}: CreateActivityBoardItemDialogProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const lastResetKeyRef = useRef<string | null>(null);
  const isInitializingRef = useRef(false);
  const prevStartDateRef = useRef<Dayjs | null>(null);

  const [loading, setLoading] = useState(false);
  const [efforts, setEfforts] = useState<EffortItem[]>([]);
  const [holidays, setHolidays] = useState<HolidayItem[]>([]);
  const [userPermissionActivities, setUserPermissionActivities] = useState<ActivityBoardItem[]>([]);

  const loggedUserName = Authentication.getLoggedUser();

  const currentUser = useMemo(() => users.find((user) => user.nameSurname === loggedUserName), [users, loggedUserName]);

  const permissionActivityTypeIds = useMemo(
    () =>
      activityTypes.filter((activityType) => activityType.isPermissionActivity).map((activityType) => activityType.id),
    [activityTypes]
  );

  const effortActivityTypeIds = useMemo(
    () => activityTypes.filter((activityType) => activityType.isEffortActivity).map((activityType) => activityType.id),
    [activityTypes]
  );

  const filteredActivityTypes = useMemo(
    () => activityTypes.filter((activityType) => !activityType.isPermissionActivity),
    [activityTypes]
  );

  const schema = useMemo(
    () =>
      yup
        .object({
          bookId: yup
            .mixed()
            .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
          projectId: yup
            .mixed()
            .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
          activityTypeId: yup
            .mixed()
            .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
          effortTrackNo: yup
            .string()
            .trim()
            .when('activityTypeId', {
              is: (value: ActivityType) => value?.isEffortActivity === true,
              then: (schema) =>
                schema.required(
                  intl.formatMessage({
                    id: 'validations.required',
                    defaultMessage: 'Bu alan boş bırakılamaz',
                  })
                ),
              otherwise: (schema) => schema.notRequired(),
            })
            .test(
              'is-valid-track-no',
              intl.formatMessage({
                id: 'validations.invalidTrackNo',
                defaultMessage: 'Girdiğiniz efor takip numarası seçili şirket ve ürün ile eşleşmiyor',
              }),
              function (value) {
                if (!value) return true;

                const { bookId, projectId } = this.parent;

                return efforts.some(
                  (effort) =>
                    effort.trackNo === value &&
                    effort.bookId === bookId?.id &&
                    effort.projectId === projectId?.id &&
                    effort.status !== EFFORT_STATUS_TYPE_ENUM.INVOICED
                );
              }
            ),
          startDate: yup
            .mixed<Dayjs>()
            .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
            .typeError(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
            .test(
              'is-dayjs',
              intl.formatMessage({ id: 'validations.invalidDateFormat', defaultMessage: 'Geçersiz tarih formatı ' }),
              (value) => !value || (dayjs.isDayjs(value) && value.isValid())
            )
            .test(
              'max-3-days-back',
              intl.formatMessage({
                id: 'validations.startDateMaxThreeDaysBack',
                defaultMessage: 'Başlangıç tarihi bugünden en fazla 3 gün geriye dönük olabilir',
              }),
              (value) => {
                if (!value) return true;

                const threeDaysAgo = dayjs().subtract(3, 'day').startOf('day');
                return value.isAfter(threeDaysAgo) || value.isSame(threeDaysAgo, 'day');
              }
            ),
          endDate: yup
            .mixed<Dayjs>()
            .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
            .typeError(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
            .test(
              'is-dayjs',
              intl.formatMessage({ id: 'validations.invalidDateFormat', defaultMessage: 'Geçersiz tarih formatı ' }),
              (value) => !value || (dayjs.isDayjs(value) && value.isValid())
            )
            .test(
              'is-after-start',
              intl.formatMessage({
                id: 'validations.endDateMustBeAfterStartDate',
                defaultMessage: 'Bitiş tarihi başlangıç tarihinden ileri bir tarih olmalıdır',
              }),
              function (value) {
                const { startDate } = this.parent;

                if (!startDate || !value) return true;

                return value.isAfter(startDate);
              }
            )
            .test(
              'same-day-as-start',
              intl.formatMessage({
                id: 'validations.endDateMustBeSameDayAsStartDate',
                defaultMessage: 'Bitiş tarihi başlangıç tarihi ile aynı gün olmalıdır',
              }),
              function (value) {
                const { startDate } = this.parent;

                if (!startDate || !value) return true;

                return value.isSame(startDate, 'day');
              }
            )
            .test(
              'max-7.5-hours',
              intl.formatMessage({
                id: 'validations.activityMaxEightHours',
                defaultMessage: 'Bir aktivite en fazla 7.5 saat olabilir',
              }),
              function (value) {
                const { startDate } = this.parent;

                if (!startDate || !value) return true;

                const diffHours = value.diff(startDate, 'hour', true);
                return diffHours <= 7.5;
              }
            )
            .test(
              'no-crossing-work-boundary',
              intl.formatMessage({
                id: 'validations.cannotCrossWorkHoursBoundary',
                defaultMessage:
                  'Aktivite saatleri mesai saatleri sınırını geçemez. Tamamen mesai içinde veya tamamen mesai dışında olmalıdır.',
              }),
              function (value) {
                const { startDate } = this.parent;

                if (!startDate || !value) return true;

                const dayOfWeek = startDate.day();

                if (dayOfWeek === 0 || dayOfWeek === 6) return true;

                const dateStr = startDate.format('DD.MM.YYYY');
                const holiday = holidays.find((holiday) => holiday.localeDateString === dateStr);

                if (holiday && !holiday.isHalfDay) return true;

                const workDayStart = startDate.hour(9).minute(0).second(0).millisecond(0);
                const workDayEnd = holiday?.isHalfDay
                  ? startDate.hour(13).minute(0).second(0).millisecond(0)
                  : startDate.hour(17).minute(30).second(0).millisecond(0);

                const startInWork =
                  (startDate.isAfter(workDayStart) || startDate.isSame(workDayStart)) &&
                  (startDate.isBefore(workDayEnd) || startDate.isSame(workDayEnd));

                const endInWork =
                  (value.isAfter(workDayStart) || value.isSame(workDayStart)) &&
                  (value.isBefore(workDayEnd) || value.isSame(workDayEnd));

                const startOutside =
                  startDate.isBefore(workDayStart) ||
                  startDate.isSame(workDayStart) ||
                  startDate.isAfter(workDayEnd) ||
                  startDate.isSame(workDayEnd);

                const endOutside =
                  value.isBefore(workDayStart) ||
                  value.isSame(workDayStart) ||
                  value.isAfter(workDayEnd) ||
                  value.isSame(workDayEnd);

                const bothInWork = startInWork && endInWork;
                const bothOutside = startOutside && endOutside;

                return bothInWork || bothOutside;
              }
            ),
          estimatedHours: yup
            .string()
            .trim()
            .notRequired()
            .matches(/^(?:0|[1-9][0-9]*)(?:\.[0-9]+)?$/, {
              message: intl.formatMessage({
                id: 'validations.onlyDigits',
                defaultMessage: 'Sadece sayı girilmelidir',
              }),
              excludeEmptyString: true,
            }),
          description: yup
            .string()
            .trim()
            .max(
              500,
              intl.formatMessage(
                { id: 'validations.maxLength', defaultMessage: 'En fazla {max} karakter girilebilir' },
                { max: 500 }
              )
            )
            .notRequired(),
          isOvertimeWork: yup.boolean().test('overtime-work-hours-check', '', function (value) {
            const { startDate, endDate } = this.parent;

            if (!startDate || !endDate) return true;

            if (isPastMonthOvertime(startDate, value)) {
              return this.createError({
                message: intl.formatMessage({
                  id: 'validations.overtimeNotAllowedForPastMonths',
                  defaultMessage: 'Geçmiş aya ait aktivitelerde Fazla Mesai seçilemez',
                }),
              });
            }

            const dayOfWeek = startDate.day();
            const isWeekendDay = dayOfWeek === 0 || dayOfWeek === 6;

            const dateStr = startDate.format('DD.MM.YYYY');
            const holiday = holidays.find((holiday) => holiday.localeDateString === dateStr);

            if (isWeekendDay) {
              if (value === false) {
                return this.createError({
                  message: intl.formatMessage({
                    id: 'validations.overtimeRequiredOnWeekend',
                    defaultMessage: 'Hafta sonu aktiviteleri için Fazla Mesai seçilmelidir',
                  }),
                });
              }

              return true;
            }

            if (holiday && !holiday.isHalfDay) {
              if (value === false) {
                return this.createError({
                  message: intl.formatMessage({
                    id: 'validations.overtimeRequiredOnHoliday',
                    defaultMessage: 'Resmi tatil günlerinde Fazla Mesai seçilmelidir',
                  }),
                });
              }

              return true;
            }

            if (holiday && holiday.isHalfDay) {
              const halfDayWorkStart = startDate.hour(9).minute(0).second(0).millisecond(0);
              const halfDayWorkEnd = startDate.hour(13).minute(0).second(0).millisecond(0);

              const isWithinHalfDayWork =
                (startDate.isAfter(halfDayWorkStart) || startDate.isSame(halfDayWorkStart)) &&
                (endDate.isBefore(halfDayWorkEnd) || endDate.isSame(halfDayWorkEnd));

              if (isWithinHalfDayWork && value === true) {
                const hasPermOverlap = userPermissionActivities.some((permissionActivity) => {
                  if (!permissionActivity.startDate || !permissionActivity.endDate) return false;

                  const permissionActivityStart = dayjs(permissionActivity.startDate);
                  const permissionActivityEnd = dayjs(permissionActivity.endDate);

                  return startDate.isBefore(permissionActivityEnd) && endDate.isAfter(permissionActivityStart);
                });

                if (!hasPermOverlap) {
                  return this.createError({
                    message: intl.formatMessage({
                      id: 'validations.overtimeNotAllowedInHalfDayWorkHours',
                      defaultMessage: 'Yarım gün tatil mesai saatleri (09:00-13:00) içinde Fazla Mesai seçilemez',
                    }),
                  });
                }
              }

              if (!isWithinHalfDayWork && value === false) {
                return this.createError({
                  message: intl.formatMessage({
                    id: 'validations.overtimeRequiredOnHoliday',
                    defaultMessage: 'Resmi tatil günlerinde Fazla Mesai seçilmelidir',
                  }),
                });
              }

              return true;
            }

            const workDayStart = startDate.hour(9).minute(0).second(0).millisecond(0);
            const workDayEnd = startDate.hour(17).minute(30).second(0).millisecond(0);

            const isWithinWorkHours =
              (startDate.isAfter(workDayStart) || startDate.isSame(workDayStart)) &&
              (endDate.isBefore(workDayEnd) || endDate.isSame(workDayEnd));

            if (isWithinWorkHours && value === true) {
              const hasPermOverlap = userPermissionActivities.some((permissionActivity) => {
                if (!permissionActivity.startDate || !permissionActivity.endDate) return false;

                const permissionActivityStart = dayjs(permissionActivity.startDate);
                const permissionActivityEnd = dayjs(permissionActivity.endDate);

                return startDate.isBefore(permissionActivityEnd) && endDate.isAfter(permissionActivityStart);
              });

              if (!hasPermOverlap) {
                return this.createError({
                  message: intl.formatMessage({
                    id: 'validations.overtimeNotAllowedInWorkHours',
                    defaultMessage: 'Mesai saatleri içinde Fazla Mesai seçilemez',
                  }),
                });
              }
            }

            if (!isWithinWorkHours && value === false) {
              return this.createError({
                message: intl.formatMessage({
                  id: 'validations.overtimeRequiredOutsideWorkHours',
                  defaultMessage: 'Mesai saatleri dışındaki saatlerde Fazla Mesai seçilmelidir',
                }),
              });
            }

            return true;
          }),
        })
        .required(),
    [intl, efforts, holidays, userPermissionActivities]
  );

  const { reset, watch, control, setValue, getValues, handleSubmit } = useForm<CreateActivityBoardItemFormParams>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  const watchBook = watch('bookId');
  const watchProject = watch('projectId');
  const watchStartDate = watch('startDate');
  const watchEndDate = watch('endDate');

  const watchBookId = watchBook?.id ?? null;
  const watchProjectId = watchProject?.id ?? null;
  const startDateYear = watchStartDate?.year();

  const initialBookId = initialData?.bookId ?? null;
  const initialProjectId = initialData?.projectId ?? null;
  const initialEffortTrackNo = initialData?.effortTrackNo ?? '';

  const initialBook = useMemo(() => {
    if (!initialBookId) return null;

    return books.find((book) => book.id === initialBookId) || null;
  }, [books, initialBookId]);

  const initialProject = useMemo(() => {
    if (!initialProjectId) return null;

    return products.find((product) => product.id === initialProjectId) || null;
  }, [products, initialProjectId]);

  const filteredProjectOptions = useMemo(() => {
    if (!watchBook) return [];

    return products
      .filter((product) => watchBook.projects.some((watchBookProduct) => watchBookProduct.id === product.id))
      .sort((firstProduct, secondProduct) => firstProduct.title.localeCompare(secondProduct.title));
  }, [products, watchBook]);

  const filteredEfforts = useMemo(
    () =>
      efforts.filter(
        (effort) =>
          effort.bookId === watchBookId &&
          effort.projectId === watchProjectId &&
          effort.status !== EFFORT_STATUS_TYPE_ENUM.INVOICED
      ),
    [efforts, watchBookId, watchProjectId]
  );

  const filteredEffortTrackNos = useMemo(() => filteredEfforts.map((effort) => effort.trackNo), [filteredEfforts]);

  const handleAddEndDateMinutes = useCallback(
    (minutes: number) => {
      const currentEndDate = getValues('endDate');
      const currentStartDate = getValues('startDate');
      const baseDate =
        currentEndDate && dayjs.isDayjs(currentEndDate) && currentEndDate.isValid()
          ? currentEndDate
          : currentStartDate && dayjs.isDayjs(currentStartDate) && currentStartDate.isValid()
            ? currentStartDate
            : null;

      if (!baseDate) return;

      setValue('endDate', baseDate.add(minutes, 'minute'), {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [getValues, setValue]
  );

  const handleClose = useCallback(() => {
    lastResetKeyRef.current = null;
    prevStartDateRef.current = null;
    isInitializingRef.current = false;
    closeDialog();
    reset(defaultValues);
    setUserPermissionActivities([]);
  }, [closeDialog, reset]);

  const fetchEfforts = useCallback(async () => {
    try {
      const resp = await apiService.getEfforts({ statusType: null, appointedConsultantId: null });

      if (resp.isSuccess && resp.data) {
        setEfforts(resp.data);
      }
    } catch {}
  }, []);

  const fetchHolidays = useCallback(async (year: number) => {
    try {
      const resp = await apiService.getHolidays(year);

      if (resp.isSuccess && resp.data) {
        setHolidays(resp.data);
      }
    } catch {}
  }, []);

  const fetchUserPermissionActivities = useCallback(async () => {
    try {
      if (!currentUser) return;

      const resp = await apiService.getActivityBoardItems({
        bookId: null,
        projectId: null,
        userId: currentUser.id,
        startDate: null,
        endDate: null,
      });

      if (resp.isSuccess) {
        const permActivities = resp.data.filter((activity) =>
          permissionActivityTypeIds.includes(activity.activityTypeId)
        );

        setUserPermissionActivities(permActivities);
      }
    } catch {}
  }, [currentUser, permissionActivityTypeIds]);

  const fetchTodayActivitiesAndGetStartDate = useCallback(async (): Promise<Dayjs> => {
    const fallback = dayjs().hour(9).minute(0).second(0).millisecond(0);

    if (!currentUser) return fallback;

    try {
      const today = dayjs();

      const resp = await apiService.getActivityBoardItems({
        bookId: null,
        projectId: null,
        userId: currentUser.id,
        startDate: today.startOf('day').toISOString(),
        endDate: today.endOf('day').toISOString(),
      });

      if (resp.isSuccess && resp.data.length > 0) {
        const activitiesWithEndDate = resp.data
          .filter((activity) => activity.endDate)
          .sort((a, b) => dayjs(b.endDate!).valueOf() - dayjs(a.endDate!).valueOf());

        if (activitiesWithEndDate.length > 0) {
          return dayjs(activitiesWithEndDate[0].endDate!);
        }
      }
    } catch {}

    return fallback;
  }, [currentUser]);

  const onSubmit = async (data: CreateActivityBoardItemFormParams) => {
    if (!data.bookId || !data.projectId || !data.activityTypeId) return;

    const postData: CreateActivityBoardItemPostFormParams = {
      bookId: data.bookId.id,
      projectId: data.projectId.id,
      activityTypeId: data.activityTypeId.id,
      effortTrackNo: data.effortTrackNo,
      startDate: data.startDate ? data.startDate.toISOString() : null,
      endDate: data.endDate ? data.endDate.toISOString() : null,
      estimatedHours: Number(data.estimatedHours) || 0,
      description: data.description,
      isOvertimeWork: data.isOvertimeWork,
    };

    try {
      setLoading(true);

      if (data.startDate && data.endDate && currentUser) {
        const newStart = data.startDate;
        const newEnd = data.endDate;

        const hasOverlap = userPermissionActivities.some((permissionActivity) => {
          if (!permissionActivity.startDate || !permissionActivity.endDate) return false;

          const permissionActivityStart = dayjs(permissionActivity.startDate);
          const permissionActivityEnd = dayjs(permissionActivity.endDate);

          return newStart.isBefore(permissionActivityEnd) && newEnd.isAfter(permissionActivityStart);
        });

        if (hasOverlap && !data.isOvertimeWork) {
          enqueueSnackbar(
            intl.formatMessage({
              id: 'validations.overtimeRequiredDuringPermission',
              defaultMessage:
                'Seçilen saat aralığında izinli olduğunuz için bu zaman diliminde yalnızca Fazla Mesai seçerek aktivite girebilirsiniz.',
            }),
            { variant: 'warning' }
          );

          setLoading(false);
          return;
        }
      }

      if (data.startDate && !data.isOvertimeWork && currentUser) {
        const activitiesResp = await apiService.getActivityBoardItems({
          bookId: null,
          projectId: null,
          userId: currentUser.id,
          startDate: data.startDate.startOf('day').toISOString(),
          endDate: data.startDate.endOf('day').toISOString(),
        });

        if (activitiesResp.isSuccess) {
          const existingRegularHours = activitiesResp.data
            .filter((activity) => !activity.isOvertimeWork)
            .reduce((sum, activity) => sum + (activity.estimatedHours || 0), 0);

          const newHours = Number(data.estimatedHours) || 0;

          if (existingRegularHours + newHours > 7.5) {
            enqueueSnackbar(
              intl.formatMessage(
                {
                  id: 'validations.dailyRegularHoursExceeded',
                  defaultMessage:
                    'Bu gün için normal mesai çalışma süresi toplamı ({totalHours} saat) 7.5 saati aşmaktadır. Mevcut giriş: {existingHours} saat.',
                },
                {
                  totalHours: (existingRegularHours + newHours).toFixed(2),
                  existingHours: existingRegularHours.toFixed(2),
                }
              ),
              { variant: 'warning' }
            );

            setLoading(false);
            return;
          }
        }
      }

      if (data.effortTrackNo && data.activityTypeId.isEffortActivity) {
        const matchingEffort = efforts.find((effort) => effort.trackNo === data.effortTrackNo);

        if (matchingEffort) {
          const maxHours = matchingEffort.effortDay * 7.5;

          const activitiesResp = await apiService.getActivityBoardItems({
            bookId: data.bookId.id,
            projectId: data.projectId.id,
            userId: null,
            startDate: null,
            endDate: null,
          });

          if (activitiesResp.isSuccess) {
            const existingHours = activitiesResp.data
              .filter(
                (activity) =>
                  activity.effortTrackNo === data.effortTrackNo &&
                  effortActivityTypeIds.includes(activity.activityTypeId)
              )
              .reduce((sum, activity) => sum + (activity.estimatedHours || 0), 0);

            const newHours = Number(data.estimatedHours) || 0;

            if (existingHours + newHours > maxHours) {
              enqueueSnackbar(
                intl.formatMessage(
                  {
                    id: 'validations.effortDayHoursExceeded',
                    defaultMessage:
                      'Bu efor takip numarasına ait toplam çalışma süresi ({totalHours} saat), efor gün limitini ({maxHours} saat = {effortDay} gün x 7.5 saat) aşmaktadır.',
                  },
                  {
                    totalHours: (existingHours + newHours).toFixed(2),
                    maxHours: String(maxHours),
                    effortDay: String(matchingEffort.effortDay),
                  }
                ),
                { variant: 'warning' }
              );

              setLoading(false);
              return;
            }
          }
        }
      }

      const response = await apiService.createActivityBoardItem(postData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="activityManaging.createActivityBoardItemCompleted"
            defaultMessage="Aktivite oluşturma işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );

        handleClose();

        if (onSuccess) {
          onSuccess();
        } else if (refresh) {
          await refresh();
        }
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    fetchEfforts();
  }, [open, fetchEfforts]);

  useEffect(() => {
    if (!open) {
      lastResetKeyRef.current = null;
      return;
    }

    if (initialBookId && !initialBook) return;
    if (initialProjectId && !initialProject) return;

    const resetKey = JSON.stringify({
      initialBookId,
      initialProjectId,
      initialEffortTrackNo,
    });

    if (lastResetKeyRef.current === resetKey) return;

    lastResetKeyRef.current = resetKey;
    isInitializingRef.current = true;

    fetchTodayActivitiesAndGetStartDate().then((computedStartDate) => {
      const endDate = computedStartDate.add(15, 'minute');

      reset({
        ...defaultValues,
        bookId: initialBook,
        projectId: initialProject,
        effortTrackNo: initialEffortTrackNo,
        startDate: computedStartDate,
        endDate,
      });

      prevStartDateRef.current = computedStartDate;

      setTimeout(() => {
        isInitializingRef.current = false;
      }, 0);
    });
  }, [
    open,
    reset,
    initialBookId,
    initialProjectId,
    initialEffortTrackNo,
    initialBook,
    initialProject,
    fetchTodayActivitiesAndGetStartDate,
  ]);

  useEffect(() => {
    if (initialData) return;

    const currentProjectId = getValues('projectId');
    const currentEffortTrackNo = getValues('effortTrackNo');

    if (currentProjectId !== null) {
      setValue('projectId', null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    if (currentEffortTrackNo) {
      setValue('effortTrackNo', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [watchBookId, initialData, getValues, setValue]);

  useEffect(() => {
    if (initialData) return;

    const currentEffortTrackNo = getValues('effortTrackNo');

    if (currentEffortTrackNo) {
      setValue('effortTrackNo', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [watchProjectId, initialData, getValues, setValue]);

  useEffect(() => {
    if (isInitializingRef.current) return;

    if (watchStartDate && prevStartDateRef.current && !watchStartDate.isSame(prevStartDateRef.current)) {
      const newEndDate = watchStartDate.add(15, 'minute');
      setValue('endDate', newEndDate, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    prevStartDateRef.current = watchStartDate ?? null;
  }, [watchStartDate, setValue]);

  useEffect(() => {
    const nextEstimatedHours =
      watchStartDate && watchEndDate && watchEndDate.isAfter(watchStartDate)
        ? watchEndDate.diff(watchStartDate, 'hour', true).toFixed(2)
        : '0';

    const currentEstimatedHours = getValues('estimatedHours');

    if (currentEstimatedHours !== nextEstimatedHours) {
      setValue('estimatedHours', nextEstimatedHours, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [watchStartDate, watchEndDate, getValues, setValue]);

  useEffect(() => {
    if (startDateYear) {
      fetchHolidays(startDateYear);
    }
  }, [startDateYear, fetchHolidays]);

  useEffect(() => {
    if (open && currentUser) {
      fetchUserPermissionActivities();
      return;
    }

    setUserPermissionActivities([]);
  }, [open, currentUser, fetchUserPermissionActivities]);

  return (
    <Dialog maxWidth="lg" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="activityManaging.createActivityBoardItem" defaultMessage="Aktivite Oluştur" />
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <DialogContentText>
            <FormattedMessage
              id="activityManaging.createActivityBoardItemText"
              defaultMessage="İlgili alanları girip devam ederek yeni bir aktivite oluşturabilirsiniz."
            />
          </DialogContentText>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="bookId">
                <FormattedMessage id="effortTracing.bookId" defaultMessage="Proje Seçimi" />
              </FormLabel>

              <Controller
                name="bookId"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Autocomplete
                    disabled={!!initialData}
                    size="small"
                    options={books}
                    value={value}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    {...renderAutoCompleteLocaleText()}
                    onChange={(_, newValue: Book | null) => onChange(newValue)}
                    getOptionLabel={(option: Book) => option.companyName ?? ''}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="projectId">
                <FormattedMessage id="effortTracing.projectId" defaultMessage="İlgili Ürün" />
              </FormLabel>

              <Controller
                name="projectId"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Autocomplete
                    disabled={!watchBook || !!initialData}
                    size="small"
                    options={filteredProjectOptions}
                    value={value}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    {...renderAutoCompleteLocaleText()}
                    onChange={(_, newValue: Project | null) => onChange(newValue)}
                    getOptionLabel={(option: Project) => option.title}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="activityTypeId">
                <FormattedMessage id="activityManaging.activityType" defaultMessage="Aktivite Türü" />
              </FormLabel>

              <Controller
                name="activityTypeId"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Autocomplete
                    size="small"
                    options={filteredActivityTypes}
                    value={value}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    {...renderAutoCompleteLocaleText()}
                    onChange={(_, newValue: ActivityType | null) => onChange(newValue)}
                    getOptionLabel={(option: ActivityType) => option.title}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="effortTrackNo">
                <FormattedMessage id="activityManaging.effortTrackNo" defaultMessage="Efor Takip No" />
              </FormLabel>

              <Controller
                name="effortTrackNo"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    disabled={!!initialData || !watchBook || !watchProject}
                    freeSolo
                    size="small"
                    options={filteredEffortTrackNos}
                    value={field.value || null}
                    onChange={(_, newValue) => field.onChange((newValue as string | null) ?? '')}
                    onInputChange={(_, newInputValue) => field.onChange(newInputValue)}
                    {...renderAutoCompleteLocaleText()}
                    slotProps={{
                      paper: {
                        sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900], minWidth: 360 },
                      },
                    }}
                    renderOption={(props, option) => {
                      const effort = filteredEfforts.find((effort) => effort.trackNo === option);
                      const consultant = effort ? users.find((user) => user.id === effort.appointedConsultantId) : null;

                      return (
                        <li {...props} key={option}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', py: 0.25 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {option}
                            </Typography>

                            {effort && (
                              <>
                                <Typography variant="caption" color="text.secondary">
                                  {intl.formatMessage({
                                    id: 'effortTracing.customerFullName',
                                    defaultMessage: 'Talep Eden Müşteri Ad Soyad',
                                  })}
                                  : {effort.customerFullName || '—'}
                                </Typography>

                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {intl.formatMessage({
                                    id: 'effortTracing.explanation',
                                    defaultMessage: 'Efor Açıklaması',
                                  })}
                                  : {effort.explanation || '—'}
                                </Typography>

                                <Typography variant="caption" color="text.secondary">
                                  {intl.formatMessage({ id: 'effortTracing.effortDay', defaultMessage: 'Efor Günü' })}:{' '}
                                  {effort.effortDay}
                                  {consultant &&
                                    ` · ${intl.formatMessage({
                                      id: 'effortTracing.appointedConsultantEmail',
                                      defaultMessage: 'Atanan Danışman',
                                    })}: ${consultant.nameSurname}`}
                                </Typography>
                              </>
                            )}
                          </Box>
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="startDate">
                <FormattedMessage id="activityManaging.startDate" defaultMessage="Başlangıç Tarihi" />
              </FormLabel>

              <Controller
                name="startDate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DateTimePicker
                    {...field}
                    ampm={false}
                    localeText={renderDatePickerLocaleText()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="endDate">
                <FormattedMessage id="activityManaging.endDate" defaultMessage="Bitiş Tarihi" />
              </FormLabel>

              <Controller
                name="endDate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Box>
                    <DateTimePicker
                      {...field}
                      onChange={(newValue) => {
                        if (newValue && field.value && newValue.hour() !== field.value.hour()) {
                          field.onChange(newValue.minute(0));
                        } else {
                          field.onChange(newValue);
                        }
                      }}
                      ampm={false}
                      localeText={renderDatePickerLocaleText()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!error,
                          helperText: error?.message,
                        },
                      }}
                    />

                    <Box sx={{ display: 'flex', gap: 0.75, mt: 1 }}>
                      {END_DATE_QUICK_ADD_MINUTES.map((minutes) => (
                        <Button
                          key={minutes}
                          type="button"
                          size="small"
                          variant="outlined"
                          disabled={!watchEndDate && !watchStartDate}
                          aria-label={`Add ${minutes} minutes to end date`}
                          onClick={() => handleAddEndDateMinutes(minutes)}
                          sx={{
                            minWidth: 44,
                            height: 28,
                            px: 1,
                            fontSize: '0.75rem',
                            lineHeight: 1,
                          }}>
                          +{minutes}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <FormLabel htmlFor="estimatedHours">
                <FormattedMessage id="activityManaging.estimatedHours" defaultMessage="Harcanan Süre (saat)" />
              </FormLabel>

              <Controller
                name="estimatedHours"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField {...field} fullWidth size="small" error={!!error} helperText={error?.message} disabled />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }} justifyItems="center">
              <Box>
                <FormLabel htmlFor="isOvertimeWork">
                  <FormattedMessage id="activityManaging.isOvertimeWork" defaultMessage="Fazla Mesai" />
                </FormLabel>
              </Box>

              <Box>
                <Controller
                  name="isOvertimeWork"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <FormControlLabel
                        control={
                          <Switch checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />
                        }
                        label={
                          field.value
                            ? intl.formatMessage({ id: 'common.yes', defaultMessage: 'Evet' })
                            : intl.formatMessage({ id: 'common.no', defaultMessage: 'Hayır' })
                        }
                      />

                      <FormHelperText error={!!error}>{error?.message}</FormHelperText>
                    </Box>
                  )}
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormLabel htmlFor="description">
                <FormattedMessage id="activityManaging.description" defaultMessage="Açıklama" />
              </FormLabel>

              <Controller
                name="description"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    error={!!error}
                    helperText={error?.message}
                    multiline
                    minRows={4}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 'auto',
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ pb: 3, px: 3 }}>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>

          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </DialogActions>
      </Box>

      <Backdrop
        sx={{ display: 'flex', flexDirection: 'column', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}>
        <img src={loaderLogoSrc} alt="loader-logo" />
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
};

export default CreateActivityBoardItemDialog;
